import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { ApiError } from "@/pages/auth/api";
import { challengeCards } from "@/pages/play/catalog";
import type { UserModelGame } from "@/pages/profile/api";
import { getProfileModelGames } from "@/pages/profile/api";
import { useI18n, type Language } from "@/shared/i18n/LanguageContext";

type ProfileModelGamesProps = {
  username: string;
};

const modelLabelById = new Map<string, string>();

for (const card of challengeCards) {
  if (card.modelId) {
    modelLabelById.set(card.modelId, card.title);
  }
  for (const variant of card.variants ?? []) {
    modelLabelById.set(variant.modelId, `${card.title} ${variant.label}`);
  }
}

function formatOptionalGameDate(value: string | null | undefined, language: Language) {
  if (!value) {
    return null;
  }

  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) {
    return null;
  }

  const formatter = new Intl.DateTimeFormat(language === "zh" ? "zh-CN" : "en-US", {
    month: language === "zh" ? "long" : "short",
    day: "numeric",
    year: "numeric",
  });
  return formatter.format(new Date(timestamp));
}

function modelLabel(modelId: string) {
  return modelLabelById.get(modelId) ?? modelId.replace(/\.(best|final)$/i, "");
}

function resultToken(
  game: UserModelGame,
  labels: ReturnType<typeof useI18n>["copy"]["profile"]["modelGames"],
) {
  if (!game.result) {
    return game.state.startsWith("GAME_OVER") ? labels.final : labels.live;
  }
  if (game.result.winner === "white") {
    return "1-0";
  }
  if (game.result.winner === "black") {
    return "0-1";
  }
  return "1/2";
}

function resultTone(game: UserModelGame) {
  if (!game.result?.winner) {
    return "draw";
  }
  return game.result.winner === game.user_color ? "win" : "loss";
}

function timeControl(game: UserModelGame) {
  return `${game.time_control.minutes}+${game.time_control.increment_seconds}`;
}

export function ProfileModelGames({ username }: ProfileModelGamesProps) {
  const { copy, language } = useI18n();
  const labels = copy.profile.modelGames;
  const [games, setGames] = useState<UserModelGame[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "loading-more" | "error">("loading");
  const [error, setError] = useState<string | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const loadMore = useCallback(async () => {
    if (!nextCursor || status === "loading" || status === "loading-more") {
      return;
    }
    setStatus("loading-more");
    setError(null);
    try {
      const page = await getProfileModelGames(username, nextCursor);
      setGames((current) => [...current, ...page.items]);
      setNextCursor(page.next_cursor ?? null);
      setStatus("ready");
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : labels.failed);
      setStatus("error");
    }
  }, [labels.failed, nextCursor, status, username]);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    setError(null);
    setGames([]);
    setNextCursor(null);

    const load = async () => {
      try {
        const page = await getProfileModelGames(username);
        if (cancelled) {
          return;
        }
        setGames(page.items);
        setNextCursor(page.next_cursor ?? null);
        setStatus("ready");
      } catch (loadError) {
        if (cancelled) {
          return;
        }
        if (loadError instanceof ApiError && loadError.status === 404) {
          setGames([]);
          setNextCursor(null);
          setStatus("ready");
          return;
        }
        setError(loadError instanceof Error ? loadError.message : labels.failed);
        setStatus("error");
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [labels.failed, username]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !nextCursor || status === "loading-more" || status === "error") {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          void loadMore();
        }
      },
      { rootMargin: "320px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [loadMore, nextCursor, status]);

  const visibleGames = useMemo(
    () =>
      games.map((game) => ({
        ...game,
        modelLabel: modelLabel(game.model_id),
        resultToken: resultToken(game, labels),
        resultTone: resultTone(game),
        createdLabel: formatOptionalGameDate(game.created_at, language),
      })),
    [games, labels, language],
  );

  if (status === "loading") {
    return (
      <div className="profile-model-games profile-model-games--loading" aria-busy="true">
        {Array.from({ length: 4 }).map((_, index) => (
          <div className="profile-model-game-skeleton" key={index} />
        ))}
      </div>
    );
  }

  if (visibleGames.length === 0 && status !== "error") {
    return <p className="profile-status">{labels.empty}</p>;
  }

  return (
    <div className="profile-model-games">
      {visibleGames.map((game) => (
        <Link className="profile-model-game" to={`/play/session/${game.game_id}`} key={game.game_id}>
          <span className="profile-model-game__result" data-tone={game.resultTone}>
            {game.resultToken}
          </span>
          <div className="profile-model-game__main">
            <strong>{game.modelLabel}</strong>
            <span>
              {labels.white}: {game.white_name} / {labels.black}: {game.black_name}
            </span>
          </div>
          <div className="profile-model-game__meta">
            <span>{timeControl(game)}</span>
            <span>
              {game.move_count} {labels.ply}
            </span>
            {game.createdLabel ? (
              <time dateTime={game.created_at ?? undefined}>{game.createdLabel}</time>
            ) : null}
          </div>
        </Link>
      ))}

      {error ? <p className="profile-status">{error}</p> : null}
      {status === "loading-more" ? <p className="profile-status">{labels.loadingMore}</p> : null}
      <div ref={sentinelRef} className="profile-scroll-sentinel" aria-hidden="true" />
    </div>
  );
}
